<template>
  <div class="mb-4 card mini-static-card">
    <div class="p-4 card-body">
      <div class="d-flex">
          <div class="numbers">

            <p
              v-if="isLoading"
              class="mb-0 skeleton skeleton-title"
              aria-hidden="true"
            ></p>
            <p v-else class="mb-0 text-md text-capitalize font-weight-bold text-analiza-dark">
              {{ title }}
              <span
                v-if="help"
                class="help-tooltip-wrapper"
                tabindex="0"
                aria-label="Mostrar ayuda"
              >
                <button
                  class="help-trigger text-analiza-medium"
                  type="button"
                  tabindex="-1"
                >
                  <font-awesome-icon :icon="faCircleInfo" />
                </button>
                <span class="custom-tooltip help-tooltip" role="tooltip">
                  {{ help }}
                </span>
              </span>
            </p>
            <div
              v-if="isLoading"
              class="skeleton skeleton-value"
              aria-hidden="true"
            ></div>
            <h5 v-else class="mb-0 font-weight-bolder text-xxl">
              {{ formattedValue.integer }}<span v-if="formattedValue.decimal" class="decimal">,{{ formattedValue.decimal }} {{ valueSuffix }}</span>
            </h5>
          </div>
          <div
            v-if="isLoading"
            class="porcentajes"
            aria-hidden="true"
          >
            <div class="skeleton skeleton-icon"></div>
            <div class="skeleton skeleton-percentage"></div>
          </div>
          <div
            v-else
            class="porcentajes percentage-tooltip-wrapper"
            tabindex="0"
          >
            <font-awesome-icon
              class="percentage-icon"
              :class="percentageIconClass"
              :icon="percentageIcon" 
            />
            <span class="text-sm font-weight-bolder">
              {{ percentage }}
            </span>
            <span class="custom-tooltip percentage-tooltip" role="tooltip">
              Periodo anterior {{ valueOld }}{{ valueSuffix }}
            </span>
          </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
  faAngleDown,
  faAngleUp,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";

export default {
  name: "MiniStatisticsCard",
  components: {
    FontAwesomeIcon,
  },
  props: {
    title:  {
      type: String,
      default: "",
    },
    value:  {
      type: [String, Number],
      default: "",
    },
    valueOld:  {
      type: [String, Number],
      default: "",
    },
    percentage: {
      type: [String, Number],
      default: "",
    },
    help: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    valueType: {
      type: String,
      default: "importe",
      validator(value) {
        return [
          "importe",
          "porcentaje",
          "otro",
          "amount",
          "percentage",
          "other",
        ].includes(value);
      },
    },
  },
  data() {
    return {
      faCircleInfo,
    };
  },
  computed: {
    isLoading() {
      return this.loading || this.value === null || this.value === undefined || this.value === "";
    },
    isPositivePercentage() {
      const normalizedPercentage = String(this.percentage)
        .replace("%", "")
        .replace(",", ".")
        .trim();
      const numericPercentage = Number(normalizedPercentage);

      if (!Number.isNaN(numericPercentage)) {
        return numericPercentage >= 0;
      }

      return !String(this.percentage).trim().startsWith("-");
    },
    percentageIcon() {
      return this.isPositivePercentage ? faAngleUp : faAngleDown;
    },
    percentageIconClass() {
      return this.isPositivePercentage ? "text-success" : "text-danger";
    },
    valueSuffix() {
      if (["importe", "amount"].includes(this.valueType)) {
        return " €";
      }

      if (["porcentaje", "percentage"].includes(this.valueType)) {
        return "%";
      }

      return "";
    },
    formattedValue() {
      const parts = String(this.value).split(',');
      if (parts.length === 2) {
        return { integer: parts[0], decimal: parts[1] };
      }
      return { integer: String(this.value), decimal: '' };
    },
  },
};
</script>

<style lang="scss" scoped>
.numbers {
  position: relative;
  width: 100%;
}

.help-trigger {
  position: relative;
  top: -5px;
  right: -5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  padding: 0;
  color: #6c757d;
  background: transparent;
  border: 0;
  line-height: 1;
  cursor: pointer;

  &:hover,
  &:focus {
    color: #344767;
    outline: none;
  }
}

.help-tooltip-wrapper {
  position: relative;
  display: inline-flex;

  &:hover,
  &:focus {
    outline: none;

    .help-tooltip {
      opacity: 1;
      transform: translate(-50%, -0.3rem);
      visibility: visible;
    }
  }
}

.porcentajes {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  width: 60px;
  cursor: pointer;
}

.percentage-tooltip-wrapper {

  &:hover,
  &:focus {
    outline: none;

    .percentage-tooltip {
      opacity: 1;
      transform: translate(-50%, -0.3rem);
      visibility: visible;
    }
  }
}

.custom-tooltip {
  position: absolute;
  z-index: 10;
  width: max-content;
  max-width: 220px;
  padding: 0.5rem 0.65rem;
  color: #fff;
  background: #344767;
  border-radius: 0.35rem;
  font-size: 0.9rem;
  font-weight: 400;
  line-height: 1.35;
  text-transform: none;
  box-shadow: 0 8px 18px rgba(20, 20, 20, 0.12);
  opacity: 0;
  pointer-events: none;
  transform: translate(-50%, 0);
  transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease;
  visibility: hidden;
}

.help-tooltip {
  bottom: 100%;
  left: 50%;
}

.percentage-tooltip {
  bottom: 100%;
  left: -50%;
}

.percentage-icon {
  margin-bottom: 0.2rem;
  font-size: 2rem;
  line-height: 1;
}

.decimal {
  font-size: 0.75em;
}

.skeleton {
  position: relative;
  overflow: hidden;
  background: #e9ecef;
  border-radius: 0.35rem;

  &::after {
    position: absolute;
    inset: 0;
    content: "";
    transform: translateX(-100%);
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.75) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: skeleton-shimmer 1.25s infinite;
  }
}

.skeleton-title {
  width: 68%;
  height: 1rem;
  margin-bottom: 0.65rem;
}

.skeleton-value {
  width: 78%;
  height: 2rem;
}

.skeleton-icon {
  width: 2rem;
  height: 2rem;
  margin-bottom: 0.45rem;
  border-radius: 50%;
}

.skeleton-percentage {
  width: 3rem;
  height: 0.85rem;
}

@keyframes skeleton-shimmer {
  100% {
    transform: translateX(100%);
  }
}
</style>
